import React, { useState, useEffect } from "react";
import { useColors } from "../../../contexts/ColorContext";
import { Card, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import api from "../../../services/api";

function VendaProdutos({ usuario, idClienteMaster }) {
  const { colors } = useColors();
  const { primaryColor, cardBackgroundColor, cardTextColor, buttonColor, buttonTextColor } = colors;
  const [produtos, setProdutos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [form, setForm] = useState({
    idCliente: "",
    idProduto: "",
    quantidade: 1,
    precoUnitario: 0,
  });

  // Só executa quando idClienteMaster existir
  useEffect(() => {
    if (!idClienteMaster) return;  // Evita buscar com null
    fetchData();
  }, [idClienteMaster]);

  const fetchData = async () => {
    setLoading(true);
    setErro("");

    try {
      const [produtosRes, clientesRes] = await Promise.all([
        api.get(`/Produtos?idClienteMaster=${idClienteMaster}`),
        api.get(`/Clientes?idClienteMaster=${idClienteMaster}`),
      ]);

      setProdutos(produtosRes.data.filter(p => p.estoque > 0));
      setClientes(clientesRes.data);

    } catch (err) {
      setErro("Erro ao carregar dados: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newForm = { ...form, [name]: value };

    if (name === "idProduto") {
      const produtoSelecionado = produtos.find(p => p.id === parseInt(value));
      if (produtoSelecionado) {
        newForm.precoUnitario = produtoSelecionado.preco;
      }
    }

    setForm(newForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErro("");

    const produtoSelecionado = produtos.find(p => p.id === parseInt(form.idProduto));

    if (!produtoSelecionado) {
      setErro("Selecione um produto válido.");
      setLoading(false);
      return;
    }

    if (form.quantidade > produtoSelecionado.estoque) {
      setErro(`Estoque insuficiente. Máximo disponível: ${produtoSelecionado.estoque}`);
      setLoading(false);
      return;
    }

    try {
      await api.post("/Produtos/vender", {
        idClienteMaster: idClienteMaster,
        idCliente: parseInt(form.idCliente),
        idProduto: parseInt(form.idProduto),
        quantidade: parseInt(form.quantidade),
        precoUnitario: parseFloat(form.precoUnitario),
      });

      window.globalAlert("Venda registrada com sucesso!", "success");
      setForm({
        idCliente: "",
        idProduto: "",
        quantidade: 1,
        precoUnitario: 0,
      });

      fetchData(); // atualiza estoque
    } catch (err) {
      setErro("Erro ao registrar venda: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const produtoAtual = produtos.find(p => p.id === parseInt(form.idProduto));
  const estoqueDisponivel = produtoAtual ? produtoAtual.estoque : 0;
  const valorTotal = form.quantidade * form.precoUnitario;

  // Enquanto carrega o ID do ClienteMaster, mostra spinner
  if (!idClienteMaster || loading) {
    return (
      <div className="text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
	    <div className="d-flex justify-content-center align-items-start w-100">
	    <Card className="p-4 shadow-sm w-100" style={{ backgroundColor: cardBackgroundColor, color: cardTextColor }}>
      <Card.Title>Venda de Produtos</Card.Title>
      <hr />

      {erro && <Alert variant="danger">{erro}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Cliente</Form.Label>
              <Form.Select
                name="idCliente"
                value={form.idCliente}
                onChange={handleChange}
                required
              >
                <option value="">Selecione o Cliente</option>
                {clientes.map((cliente) => (
                 <option key={cliente.id} value={cliente.id}>
                {cliente.nome} ({cliente.telefone.replace(/^55/, "")})
                </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Produto</Form.Label>
              <Form.Select
                name="idProduto"
                value={form.idProduto}
                onChange={handleChange}
                required
              >
                <option value="">Selecione o Produto</option>
                {produtos.map((produto) => (
                  <option key={produto.id} value={produto.id}>
                    {produto.nome} (Estoque: {produto.estoque})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Quantidade</Form.Label>
              <Form.Control
                type="number"
                name="quantidade"
                value={form.quantidade}
                onChange={handleChange}
                min="1"
                max={estoqueDisponivel}
                required
              />
              <Form.Text className="text-muted">
                Estoque disponível: {estoqueDisponivel}
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Preço Unitário (R$)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                name="precoUnitario"
                value={form.precoUnitario}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Valor Total (R$)</Form.Label>
              <Form.Control
                type="text"
                value={valorTotal.toFixed(2).replace(".", ",")}
                readOnly
                disabled
              />
            </Form.Group>
          </Col>
        </Row>

	        <Button
	          variant="primary"
	          type="submit"
	          className="w-100 mt-3"
	          disabled={loading || !form.idProduto || !form.idCliente}
	          style={{ backgroundColor: buttonColor, borderColor: buttonColor, color: buttonTextColor }}
	        >
          {loading ? <Spinner animation="border" size="sm" /> : "Registrar Venda"}
        </Button>
      </Form>
    </Card>
    </div>

  );
}

export default VendaProdutos;
